import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/GlobalProvider";
import { FaEye, FaEyeSlash, FaSpinner } from "react-icons/fa";
import axios from "axios";
import { SETTINGS_CRUD_API } from "../../../hooks/APIHooks";

const Signin = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [companyData, setCompanyData] = useState({ companyName: "" });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    emailAddress: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(SETTINGS_CRUD_API);
      if (response.data && !response.data.error) {
        setCompanyData(response.data);
      }
    } catch (err) {
      console.error("Error fetching company settings:", err);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError("");
  };

  const validateForm = () => {
    if (!formData.emailAddress.trim() || !formData.password.trim()) {
      setError("Please enter both email/phone and password");
      return false;
    }

    const isValidEmail = /\S+@\S+\.\S+/.test(formData.emailAddress);
    const isValidPhone = /^[0-9]{10}$/.test(formData.emailAddress);

    if (!isValidEmail && !isValidPhone) {
      setError("Please enter a valid email or 10-digit phone number");
      return false;
    }

    return true;
  };

  const onSignInPress = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const result = await signIn(formData.emailAddress, formData.password);
      if (result.success) {
        navigate("/user/dashboard");
      } else {
        setError(result.message || "Sign in failed. Please check your credentials.");
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
      console.error("Signin error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !loading) {
      onSignInPress();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {companyData.companyName || "Welcome Back"}
          </h1>
          <p className="text-gray-600">Sign in to your account</p>
        </div>

        {/* Form */}
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email or Phone Number
            </label>
            <input
              type="text"
              value={formData.emailAddress}
              placeholder="Enter email or phone number"
              onChange={(e) => handleInputChange("emailAddress", e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                placeholder="Enter your password"
                onChange={(e) => handleInputChange("password", e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10 disabled:opacity-50"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                disabled={loading}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          {/* Terms Notice */}
          <div className="text-xs text-center text-gray-500">
            By continuing, you agree to our{" "}
            <span
              className="text-blue-600 cursor-pointer hover:underline"
              onClick={() => !loading && navigate("/termsconditions")}
            >
              Terms & Conditions
            </span>
          </div>

          {/* Sign In Button with Loading State */}
          <button
            onClick={onSignInPress}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" />
                <span>Signing In...</span>
              </>
            ) : (
              <span>Sign In</span>
            )}
          </button>
        </div>

        {/* Links */}
        <div className="text-center space-y-3 mt-6 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-600">
            Don't have an account?{" "}
            <Link
              to="/signup"
              className="text-blue-600 font-semibold hover:underline"
            >
              Sign Up
            </Link>
          </p>
          <p className="text-sm text-gray-600">
            Forgot your password?{" "}
            <Link
              to="/forgot-password"
              className="text-blue-600 font-semibold hover:underline"
            >
              Reset Password
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signin;